import {
  getPriceWithCurrency, getPriceWithTaxes,
  getQuotePriceWithDiscounts, getQuotePriceWithTaxes,
  getTotalPrice,
  toFixedPrice
} from "../../misc/helpers";
import {
  DISCOUNT_DYNAMIC_PROPERTIES,
  DISCOUNT_MAIN_TYPES,
  DISCOUNT_SECTION_TYPES,
  DISCOUNT_TYPES
} from "./discounstInterface";
import {PMT} from "../../misc/excelFunctions";
import {getPercentage} from "../../misc/misc2";
import store from "../../store";
import {hardCodeIsRenuit} from "../../misc/fabricatorHardcode";

/*
* allow without
* */
export const shouldDisplayPriceWorksheet = (
  props,
  withoutReceived = false,
  showForClient = false,
  fromReceipt = false,
) => {
  const { discountsReceived, discountSections } = props;
  const hasNoDiscountSections = !discountSections.length;

  // Guard: hide based on discount state
  if (withoutReceived) {
    if (discountsReceived && hasNoDiscountSections) {
      return false
    }
  } else if (!discountsReceived || hasNoDiscountSections) {
    return false;
  }

  // Guard: hide if user lacks Price Worksheet permission
  const isPermissionBlocked = Array.isArray(document.dashboardUserPermissions)
    && document.dashboardUserPermissions.some(({ ProtectedAreaId, AccessRights }) => {
      return ProtectedAreaId === 9001 && AccessRights === 0;
    }); // Price Worksheet on Visualizer;

  if (isPermissionBlocked) return false;

  // Show if backend, or client-facing with a valid job/receipt context
  const isClientVisible = showForClient && (document.kvConfig.jobId || fromReceipt);
  return document.kvConfig.isBackend || isClientVisible;
};

export const shouldUseMultipleWorkSheets = (workSheetsCount) => {
  return workSheetsCount > 1 || hardCodeIsRenuit()
}

export const calculateDiscountOnlyForOneRoom = (discount, showAllQuotesCartItemsList) => {
  const parsedOptions = typeof discount.wizardOptions === "string"
    ? JSON.parse(discount.wizardOptions)
    : {};
  if (!parsedOptions || !parsedOptions.forOneRoom || parsedOptions.forOneRoom === "allRooms") {
    // false, or it doesn't exist, or it was selected from dropdown.
    // it is a default behaviour, all rooms is checked, subtotal is calculating from all rooms by the default
    return false
  } else if (parsedOptions.forOneRoom === "dynamic") { // this is new, someone checked it as dynamic, so now we to detect
    return !showAllQuotesCartItemsList
  } else { // any other case (if it is true) - we display One Room, because it was a checkbox earlier.
    return true
  }
}

const getDiscountSubTotalPriceForMultipleWorkSheets = (props) => {
  const {quotesState, wizardState} = store.getState();
  const {quotes} = quotesState;
  const {allWizardsValues} = wizardState;
  const [, discountSection, discount,,,forQuoteIndex,,,,showAllQuotesCartItemsList] = props;

  return quotes.reduce((total, quote , quoteIndex) => {
    const calculateAllRooms = !calculateDiscountOnlyForOneRoom(discount, showAllQuotesCartItemsList);
    if (!calculateAllRooms && !discountSectionExistsInCurrentWizardSelections(discountSection, allWizardsValues, quoteIndex)) {
      return total
    }
    if (quoteIndex === forQuoteIndex) {
      props[5] = quoteIndex; // set workSheetsCount to 1 to prevent multiple calls // calculate total for this room without discounts, then calculate discounts
      props[7] = true; // prevent multiple calls // calculate total for this room without discounts, then calculate discounts
      const {discountSubTotal} = getDiscountSubTotalPrice(...props);
      total += +discountSubTotal;
      return total
    } else if (calculateAllRooms) {
      total += getTotalPrice(quoteIndex, true).materialPrice; // include discounts
    }
    return total
  }, 0)
}

export const getDiscountSubTotalPrice = (...props) => {

  const [
    discountSections,
    discountSection,
    discount,
    withoutCurrent = false,
    customAddedDiscounts = [],
    forQuoteIndex = null,
    hasMultipleWorkSheets,
    fromSubTotal,
    fabricatorData,
    showAllQuotesCartItemsList,
    quotes,
  ] = props;

  let totalPrice = 0;

  if (typeof forQuoteIndex === "number") {
    const isSubTotal = discount.isSubTotal || discount.discountType === DISCOUNT_MAIN_TYPES.subTotal;
    if (isSubTotal && !fromSubTotal) {
      const discountSubTotal = getDiscountSubTotalPriceForMultipleWorkSheets(props);
      const fullTotalPriceObject = getTotalPrice(forQuoteIndex, true);
      return {
        discountSubTotalWithTaxes: toFixedPrice(getPriceWithTaxes({
          ...fullTotalPriceObject,
          materialPrice: discountSubTotal,
        })),
        discountSubTotal: toFixedPrice(discountSubTotal)
      }
    } else {
      const {wizardState} = store.getState();
      const {allWizardsValues} = wizardState;
      if (discountSectionExistsInCurrentWizardSelections(discountSection, allWizardsValues, forQuoteIndex)) {
        totalPrice = getTotalPrice(forQuoteIndex, false);
      }
    }
  } else {
    totalPrice = getTotalPrice(null, false);
  }

  const allDiscounts = getDiscountsForSection(discountSection, customAddedDiscounts);
  let ignoreDiscounts = allDiscounts.filter(currentDiscount => {
    if (!shouldCalculateDiscount(currentDiscount, forQuoteIndex, hasMultipleWorkSheets)) {
      return false
    }
    return withoutCurrent
      ? currentDiscount.order >= discount.order
      : currentDiscount.order > discount.order
  });
  discountSections.forEach(disSection => {
    if (disSection.order > discountSection.order) {
      ignoreDiscounts = [...ignoreDiscounts, ...disSection.discounts] // ignore other sections
    }
  });
  customAddedDiscounts.forEach(customDiscount => {
    const section = discountSections.find(sec => sec.id === customDiscount.categoryId);
    if (!section) {
      return
    }
    if (section.order > discountSection.order
      || (hasMultipleWorkSheets && Number.isInteger(forQuoteIndex) && (customDiscount.quoteIndex !== forQuoteIndex))) {
      ignoreDiscounts = [...ignoreDiscounts, customDiscount] // ignore other custom discounts
    }
  });

  let grandTotal = 0;

  if (fabricatorData.isRenuit) {
    const isSubTotal = discount.isSubTotal || discount.discountType === DISCOUNT_MAIN_TYPES.subTotal;
    if (isSubTotal && !calculateDiscountOnlyForOneRoom(discount, showAllQuotesCartItemsList)) {
      grandTotal = quotes.reduce((total, _q, qIndex) => {
        const quoteTotalPrice = getTotalPrice(qIndex, false);
        const quoteGrandTotal = getQuotePriceWithDiscounts(quoteTotalPrice, ignoreDiscounts, false, qIndex);
        return total + quoteGrandTotal;
      }, 0);
    } else {
      grandTotal = getQuotePriceWithDiscounts(totalPrice, ignoreDiscounts, false, forQuoteIndex);
    }
  } else {
    grandTotal = getQuotePriceWithDiscounts(totalPrice, ignoreDiscounts, false, forQuoteIndex);
  }

  return {
    discountSubTotalWithTaxes: toFixedPrice(getPriceWithTaxes({
      ...totalPrice,
      materialPrice: grandTotal,
    })),
    discountSubTotal: toFixedPrice(grandTotal)
  }
};

export const discountIsCashAndSelected = (discount, selected) => {
  const discountSelected = typeof selected !== "undefined" ? selected : discount.selected;
  return discountSelected && (discount.type === DISCOUNT_TYPES.price) && (discount.name && discount.name.includes("Cash"))
}

export const someDiscountIsCashAndSelected = (customAddedDiscounts, discountSections) => {
  return customAddedDiscounts.some(customDiscount => {
    return customDiscount.selected && customDiscount.name && customDiscount.name.includes("Cash")
  }) || discountSections.some(section => {
    return section.discounts.some(dis => discountIsCashAndSelected(dis))
  })
}

export const someDiscountIsSubTotalAndSelected = (discountSections, activeQuoteIndex, hasMultipleWorkSheets) => {
  return discountSections.some(discountSection => {
    return discountSection.discounts.some(discount => {
      const realSelected = getDiscountCurrentValue(
        discount,
        "selected",
        activeQuoteIndex,
        hasMultipleWorkSheets
      );
      return realSelected && (discount.discountType === DISCOUNT_MAIN_TYPES.subTotal) && (discount.name === "Price Good for Today")
    })
  })
}

export const getDiscountsForSection = (discountSection, customAddedDiscounts) => {
  const customDiscounts = customAddedDiscounts.filter(dis => {
    return dis.selected && (dis.categoryId === discountSection.id)
  })
  return [...discountSection.discounts, ...customDiscounts];
}

export const calculateCellValue = ({discountSections, financePercentage, fabricatorSettings}, numberOfMonths = 0, finalPrice = 0, forQuoteIndex, hasMultipleWorkSheets, cellsAreDisabled = false) => {
  if (cellsAreDisabled) {
    return "Not Applicable"
  }
  if (!numberOfMonths || !finalPrice) {
    return "N/A"
  }
  const fixedDeposit = getCustomDepositAmount(discountSections, finalPrice, null, forQuoteIndex, hasMultipleWorkSheets) || 0;
  const rate = financePercentage / 100 / 12; // Converts the annual interest rate into a monthly rate by dividing it by 12
  const nper = numberOfMonths; // The number of months in the repayment term
  const pv = -(finalPrice - fixedDeposit); // The present value (loan amount to be repaid). This is the negative of:
  // finalPrice - Today's final price of the good.
  // fixedDeposit - A $1000 deposit applied to the total loan amount.

  // const cellValue = pv * rate / (1 - Math.pow((1 + rate), -nper)); // this works fine, but the func below returns the same
  const cellValue = PMT(rate, nper, pv);

  return getPriceWithCurrency(fabricatorSettings, cellValue, null, false, true)
}

export const getNextCustomDiscountOrder = (fromDiscount) => {
  return +(fromDiscount.order + 0.01).toFixed(2)
}

export const getFilteredSections = (discountSections) => {
  return discountSections.filter(section => {
    return section.type !== DISCOUNT_SECTION_TYPES.finance
  })
}

export const isSaveOnlyDiscount = (discount) => {
  return discount.discountType === DISCOUNT_MAIN_TYPES.finance
    || discount.discountType === DISCOUNT_MAIN_TYPES.deposit
    || discount.discountType === DISCOUNT_MAIN_TYPES.financedAmount
}

export const shouldDisplayDiscount = (discount, forQuoteIndex, hasMultipleWorkSheets, ignoreDiscounts =[]) => {
  if (isSaveOnlyDiscount(discount) && !(
    ignoreDiscounts.length === 1
    && (ignoreDiscounts[0].discountType === DISCOUNT_MAIN_TYPES.financedAmount)
    && (discount.discountType === DISCOUNT_MAIN_TYPES.deposit)) // here we don't skip deposit if we are calculating max value for down payment
  ) {
    return false
  }
  if (discount.nestedParent && hasMultipleWorkSheets && Number.isInteger(forQuoteIndex) && (discount.quoteIndex !== forQuoteIndex)) {
    return false
  }
  return getDiscountCurrentValue(discount, "selected", forQuoteIndex, hasMultipleWorkSheets)
}

export const shouldCalculateDiscount = (discount, forQuoteIndex, hasMultipleWorkSheets, ignoreDiscounts = []) => {
  if (discount.isRidged) {
    const neededApproveOptions = getDiscountCurrentValue(discount, "approveOptions", forQuoteIndex, hasMultipleWorkSheets);
    const approveOptions = neededApproveOptions ? JSON.parse(neededApproveOptions) : null;
    if (approveOptions && approveOptions.options && approveOptions.options.length && !approveOptions.selectedOption) {
      return false
    }
  }
  return shouldDisplayDiscount(discount, forQuoteIndex, hasMultipleWorkSheets, ignoreDiscounts) && !discount.isSubTotal
}

export const getDepositDiscount = (discountSections) => {
  let depositDiscount = null;
  discountSections.forEach(discountSection => {
    discountSection.discounts.forEach(discount => {
      if (discount.discountType === DISCOUNT_MAIN_TYPES.deposit) {
        depositDiscount = discount;
      }
    })
  });
  return depositDiscount
}

/**
 *
 * @param discountSections {array}
 * @param grandTotal {number}
 * @param fromDiscount {discountInterface}
 * @param forQuoteIndex {number}
 * @param hasMultipleWorkSheets {boolean}
 * @returns {null|string|number}
 */
export const getCustomDepositAmount = (discountSections, grandTotal = 0, fromDiscount = null, forQuoteIndex = 0, hasMultipleWorkSheets = false) => {
  const depositDiscount = fromDiscount || getDepositDiscount(discountSections);
  if (!depositDiscount) {
    return null
  }
  const amount = getDiscountCurrentValue(depositDiscount, "amount", forQuoteIndex, hasMultipleWorkSheets);
  const realType = getDiscountCurrentValue(depositDiscount, "type", forQuoteIndex, hasMultipleWorkSheets);
  if (realType === DISCOUNT_TYPES.price) {
    return amount
  }
  const priceTaken = getPercentage(grandTotal, amount) // for ex. 25% from 2700 = 625
  return +(toFixedPrice(Math.round10(priceTaken * 100) / 100));
}

export const getDepositForCurrentQuote = (discountSections, allWizardsValues, quoteIndex, hasMultipleWorkSheets, discountType = DISCOUNT_MAIN_TYPES.deposit) => {
  return discountSections.reduce((total, discountSection) => {
    if (!discountSectionExistsInCurrentWizardSelections(discountSection, allWizardsValues, quoteIndex)) {
      return total
    }
    const depositDiscount = discountSection.discounts.find(discount => (discount.discountType === discountType));
    if (depositDiscount) {
      const realType = getDiscountCurrentValue(depositDiscount, "type", quoteIndex, hasMultipleWorkSheets);
      const realAmount = getDiscountCurrentValue(depositDiscount, "amount", quoteIndex, hasMultipleWorkSheets);

      if (realType === DISCOUNT_TYPES.price) {
        return (total || 0) +realAmount
      }
      const customDepositAmount = calculateDeposit(discountSections, depositDiscount, quoteIndex, hasMultipleWorkSheets);
      return (total || 0) + customDepositAmount
    }
    return total
  }, null);
}

export const getDepositAmount = (discountSections, grandTotal, fabricatorData, forQuoteIndex = null) => {
  let customDeposit = null;
  const {priceWorksheetState, quotesState, wizardState, fabricatorState} = store.getState();
  const {workSheetsCount} = priceWorksheetState;
  if (shouldUseMultipleWorkSheets(workSheetsCount) && !fabricatorState.isRenuit) { // renuit has same deposit for all rooms
    customDeposit = quotesState.quotes.reduce((total, quote , quoteIndex) => {
      if (Number.isInteger(forQuoteIndex) && quoteIndex !== forQuoteIndex) {
        return total
      }
      const depositForCurrentQuote = getDepositForCurrentQuote(discountSections, wizardState.allWizardsValues, quoteIndex, true);
      if (depositForCurrentQuote !== null) {
        return (total || 0) + depositForCurrentQuote
      }
      return total
    }, null)
  } else {
    customDeposit = getCustomDepositAmount(discountSections, grandTotal);
  }
  if (customDeposit !== null) {
    return customDeposit
  }
  return fabricatorData.settings.enableDeposit && fabricatorData.settings.deposit
    ? Math.floor((grandTotal * (fabricatorData.settings.deposit / 100)) * 100) / 100
    : 0
}

export const getTotalForDeposit = (forQuoteIndex, hasMultipleWorkSheets) => {
  return hasMultipleWorkSheets && !hardCodeIsRenuit()
    ? getTotalPrice(forQuoteIndex)
    : getTotalPrice();
}

export const calculateDeposit = (discountSections, discount, forQuoteIndex, hasMultipleWorkSheets) => {
  const totalPrice = getTotalForDeposit(forQuoteIndex, hasMultipleWorkSheets);
  const grandTotal = getQuotePriceWithTaxes(totalPrice);
  return getCustomDepositAmount(discountSections, grandTotal, discount, forQuoteIndex, hasMultipleWorkSheets);
}

export const toSaveAndRestoreWorksheetProperties = [
  "financePercentage",
  "customAddedDiscounts",
  "financeActiveDiscount",
  "financePaymentType",
];

export const getPriceWorkSheetSaveValues = (priceWorksheetState) => {
  const selectedDiscounts = [];
  priceWorksheetState.discountSections.forEach(disSection => {
    if (disSection.type === DISCOUNT_SECTION_TYPES.finance) {
      return
    }
    disSection.discounts.forEach(discount => {
      if (discount.selected || (
        discount.roomValues
        && Object.keys(discount.roomValues).some(quoteIndex => discount.roomValues[quoteIndex].selected)
      )) {
        selectedDiscounts.push(Object.assign({}, discount, {
          categoryId: disSection.id,
          categoryName: disSection.name,
          categoryOrder: disSection.order,
          wizardLogic: discount.discountType === DISCOUNT_MAIN_TYPES.lowesIME ? disSection.wizardLogic : "",
        }));
      }
    })
  });
  return {
    selectedDiscounts,
    ...toSaveAndRestoreWorksheetProperties.reduce((acc, property) => {
      return { ...acc, [property]: priceWorksheetState[property] };
    }, {}),
  };
}

// WIZARD LOGIC START

/**
 * returns different worksheets for different wizard selections. if no logic added - return true
 * @param discountSections {[discountSectionsInterface]}
 * @param allWizardsValues {[wizardAllObjectInterface]}
 * @param quoteIndex {number}
 * @return {[discountSectionsInterface]}
 */
export const getDiscountSectionsForCurrentWizard = (discountSections, allWizardsValues, quoteIndex) => {
  return discountSections.filter(discountSection => {
    return discountSectionExistsInCurrentWizardSelections(discountSection, allWizardsValues, quoteIndex)
  })
}

/**
 * returns different worksheets for different wizard selections. if no logic added - return true
 * @param discountSection {discountSectionsInterface}
 * @param allWizardsValues {[wizardAllObjectInterface]}
 * @param quoteIndex {number}
 * @return {boolean}
 */
export const discountSectionExistsInCurrentWizardSelections = (discountSection, allWizardsValues, quoteIndex) => {
  if (!discountSection.wizardLogic) {
    return true
  }
  const wizardLogic = JSON.parse(discountSection.wizardLogic);
  if (!Array.isArray(wizardLogic.displayOptions) || !wizardLogic.displayOptions.length) {
    return true
  }
  return wizardLogic.displayOptions.some(displayOption => {
    return allWizardsValues.some(allWizardObject => {
      const dashboardWizardId = allWizardObject.wizardItemId % 1000;
      // console.log("dashboardWizardId", dashboardWizardId);
      if (allWizardObject.quoteIndex !== quoteIndex) {
        return false
      }
      if ((allWizardObject.uniqueHashId && displayOption.forWizardHashId && (allWizardObject.uniqueHashId !== displayOption.forWizardHashId))
        && (dashboardWizardId !== displayOption.forWizardId || allWizardObject.visualizer !== displayOption.forVisualizer)) {
        return false
      }
      return Object.keys(allWizardObject.wizardSelectedValues).some(key => {
        return allWizardObject.wizardSelectedValues[key].wizardGroup
          && (allWizardObject.wizardSelectedValues[key].wizardGroup.id === displayOption.forWizardGroupId)
      })
    })
  })
}

/**
 * parsed logic
 * @param discountSection {discountSectionsInterface}
 * @param index {number}
 * @return {discountWizardLogicInterface}
 */
export const getParsedWizardLogic = (discountSection, index = 0) => {
  return discountSection && discountSection.wizardLogic
    ? JSON.parse(discountSection.wizardLogic)
    : {index, displayOptions: [], forLowesIME: false}
}

/**
 * fabricatorData.receiptSettings?.homeDepotReceipt gates all the Home Depot receipt-specific rendering, but
 * fabricatorData.receiptSettings?.homeDepotReceiptWorkSheets (a map of wizardLogic.index -> checked, saved by
 * Dashboard's ReceiptSettingsHomeDepotWorkSheetsSelect.tsx) can further restrict it to specific Price Work
 * Sheets. A missing/empty homeDepotReceiptWorkSheets means "every worksheet" (unrestricted, same as before);
 * once it has entries, only indexes not explicitly set to false are considered "checked" - matching the
 * dashboard component's own default. When restricted, Home Depot receipt is only treated as active if at
 * least one checked worksheet's discountSection is currently visible (discountSectionExistsInCurrentWizardSelections)
 * for at least one quote/room - receipts always aggregate all rooms into a single document.
 * @param fabricatorData {object}
 * @param discountSections {[discountSectionsInterface]}
 * @param allWizardsValues {[wizardAllObjectInterface]}
 * @param quotes {array}
 * @return {boolean}
 */
export const shouldUseHomeDepotReceipt = (fabricatorData, discountSections, allWizardsValues, quotes) => {
  if (!fabricatorData || !fabricatorData.receiptSettings?.homeDepotReceipt) {
    return false;
  }
  const homeDepotReceiptWorkSheets = fabricatorData.receiptSettings?.homeDepotReceiptWorkSheets;
  if (!homeDepotReceiptWorkSheets || typeof homeDepotReceiptWorkSheets !== "object"
    || !Object.keys(homeDepotReceiptWorkSheets).length) {
    return true;
  }
  if (!Array.isArray(discountSections) || !discountSections.length) {
    return true; // nothing to check the selected worksheets against, fall back to unrestricted
  }
  const quoteIndexes = Array.isArray(quotes) && quotes.length ? quotes.map((quote, quoteIndex) => quoteIndex) : [0];
  return discountSections.some(discountSection => {
    const wizardLogic = getParsedWizardLogic(discountSection);
    const isWorkSheetSelected = homeDepotReceiptWorkSheets[wizardLogic.index] !== false;
    if (!isWorkSheetSelected) {
      return false
    }
    return quoteIndexes.some(quoteIndex => {
      return discountSectionExistsInCurrentWizardSelections(discountSection, allWizardsValues, quoteIndex);
    });
  });
}

export const updateDiscountCurrentValues = (discount, quoteIndex) => {
  if (!discount.roomValues) { // just check
    return discount
  }
  DISCOUNT_DYNAMIC_PROPERTIES.forEach(property => {
    discount[property] = getDiscountCurrentValue(discount, property, quoteIndex, true)
  });
  return discount
}

export const getDiscountCurrentValue = (discount, property, quoteIndex, hasMultipleWorkSheets = false) => {
  if (!hasMultipleWorkSheets || discount.nestedParent) { // nested are filtered by quoteIndex
    return discount[property]
  }
  return discount.roomValues && discount.roomValues[quoteIndex]
    ? discount.roomValues[quoteIndex][property]
    : discount.defaultValues
      ? discount.defaultValues[property]
      : discount[property]
}

// WIZARD LOGIC END
